import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import Button from "../../DesignSystem/button";
import AuthService from "../../../services/auth.service";
import { UpdateUserInformationByIdPayload } from "../../../interfaces/auth.interface";
import Dropdown from "../../DesignSystem/dropdown";

interface UserDetailsModalProps {
  user: any;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  organizations: any[];
}

interface UserData {
  userId: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
  organizationName?: string;
  organizationId?: string;
  contactNo?: string;
  address?: string;
  country?: string;
  state?: string;
  city?: string;
  zipcode?: string;
}

interface ModeifiedFields {
  [key: string]: string;
}

interface EditableFieldProps {
  label: string;
  value: string;
  isEditing: boolean;
  isEditable: boolean;
  onEdit: (value: string) => void;
}

const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  isEditing,
  isEditable,
  onEdit,
}) => {
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  return (
    <div className="field-container">
      <div className="field-label">{label}</div>
      {isEditing && isEditable ? (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => onEdit(editValue)}
          className="field-value"
        />
      ) : (
        <div className="field-value">{value || "N/A"}</div>
      )}
    </div>
  );
};

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  isOpen,
  onClose,
  onUpdate,
  organizations,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedUser, setEditedUser] = useState<UserData | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [modifiedFields, setModifiedFields] = useState<Set<keyof UserData>>(
    new Set()
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [displayOrgId, setDisplayOrgId] = useState<string>("");

  useEffect(() => {
    if (user) {
      setEditedUser(user);
      setSelectedOrganization(user.organization_name);
      setDisplayOrgId(user.organization_id.toString());
    }
  }, [user]);

  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
      setHasChanges(false);
      setModifiedFields(new Set());
      if (user) {
        setSelectedOrganization(user.organization_name);
        setDisplayOrgId(user.organization_id.toString());
      }
    }
  }, [isOpen, user]);

  if (!user || !editedUser) return null;

  const handleFieldEdit = (field: keyof UserData, value: string) => {
    const updatedUser = { ...editedUser, [field]: value };
    setEditedUser(updatedUser);
    if (value !== user[field]) {
      setModifiedFields((prev) => new Set(prev).add(field));
    } else {
      setModifiedFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(field);
        return newSet;
      });
    }
    setHasChanges(true);
  };

  const handleOrganizationChange = (value: string) => {
    try {
      const selectedOrg = organizations.find(
        (org) => org.organization_name === value
      );
      setSelectedOrganization(value);
      handleFieldEdit("organizationName", value);
      setModifiedFields((prev) => {
        const newSet = new Set(prev);
        newSet.add("organizationName");
        return newSet;
      });
      if (selectedOrg) {
        setDisplayOrgId(selectedOrg.id?.toString() || "");
      }
      setHasChanges(true);
    } catch (error) {
      console.error("Error handling organization change:", error);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const updates = Array.from(modifiedFields).reduce(
        (acc, field) => {
          if (editedUser && editedUser[field] !== user[field]) {
            if (field === "organizationName") {
              return {
                ...acc,
                company: selectedOrganization,
              };
            }
            return {
              ...acc,
              [field]: editedUser[field],
            };
          }
          return acc;
        },
        { userId: user.id } as UpdateUserInformationByIdPayload
      );

      if (selectedOrganization !== user.organization_name) {
        updates.organization = selectedOrganization;
      }

      if (Object.keys(updates).length > 1) {
        console.log(updates);
        await AuthService.editUserDataById(updates);
        setIsEditing(false);
        setHasChanges(false);
        setModifiedFields(new Set());
        onUpdate?.();
        onClose();
      }
    } catch (error) {
      console.error("Error saving user information changes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedUser({ ...user });
    setIsEditing(false);
    setHasChanges(false);
    setModifiedFields(new Set());
    setSelectedOrganization(user.organization_name);
    setDisplayOrgId(user.organization_id);
  };

  const UNEDITABLE_FIELDS = ["organization_id"];

  return (
    <Dialog
      open={isOpen}
      keepMounted
      onClose={onClose}
      classes={{
        root: "dialog-root user-details-modal",
        paper: "dialog-paper",
      }}
    >
      <DialogTitle
        classes={{
          root: "dialog-title user-details-title",
        }}
      >
        <div className="title-container">
          <label>User {user.id}</label>
          <div className="actions">
            {!isEditing && (
              <EditIcon
                className="edit-icon"
                onClick={() => setIsEditing(true)}
              />
            )}
            <CloseIcon onClick={onClose} className="close-icon" />
          </div>
        </div>
      </DialogTitle>

      <DialogContent className="dialog-content">
        <div className="user-info-content">
          <div className="info-section">
            {Object.entries({
              firstName: "First Name",
              lastName: "Last Name",
              username: "Username",
              email: "Email",
            }).map(([field, label]) => (
              <EditableField
                key={field}
                label={label}
                value={editedUser[field as keyof UserData] || ""}
                isEditing={isEditing}
                isEditable={!UNEDITABLE_FIELDS.includes(field)}
                onEdit={(value) =>
                  handleFieldEdit(field as keyof UserData, value)
                }
              />
            ))}
            <div className="company-org-container">
              <div className="company-field">
                {isEditing ? (
                  <div className="field-container">
                    <div className="field-label">Company</div>
                    <Dropdown
                      className="company-dropdown"
                      value={selectedOrganization}
                      options={organizations}
                      valueKey="organization_name"
                      textKey="organization_name"
                      icon=""
                      disabled={false}
                      onChange={handleOrganizationChange}
                    />
                  </div>
                ) : (
                  <EditableField
                    label="Company"
                    value={editedUser.organizationName || ""}
                    isEditing={false}
                    isEditable={true}
                    onEdit={() => {}}
                  />
                )}
              </div>
              <div className="org-id-field">
                <EditableField
                  label="Org ID"
                  value={displayOrgId}
                  isEditing={isEditing}
                  isEditable={false}
                  onEdit={() => {}}
                />
              </div>
            </div>
            <EditableField
              label="Contact Number"
              value={editedUser.contactNo || ""}
              isEditing={isEditing}
              isEditable={true}
              onEdit={(value) => handleFieldEdit("contactNo", value)}
            />
            <EditableField
              label="Address"
              value={editedUser.address || ""}
              isEditing={isEditing}
              isEditable={true}
              onEdit={(value) => handleFieldEdit("address", value)}
            />
            <EditableField
              label="City"
              value={editedUser.city || ""}
              isEditing={isEditing}
              isEditable={true}
              onEdit={(value) => handleFieldEdit("city", value)}
            />
            <div className="state-zip-container">
              <div className="state-field">
                <EditableField
                  label="State"
                  value={editedUser.state || ""}
                  isEditing={isEditing}
                  isEditable={true}
                  onEdit={(value) => handleFieldEdit("state", value)}
                />
              </div>
              <div className="zip-field">
                <EditableField
                  label="Zipcode"
                  value={editedUser.zipcode || ""}
                  isEditing={isEditing}
                  isEditable={true}
                  onEdit={(value) => handleFieldEdit("zipcode", value)}
                />
              </div>
            </div>
            <EditableField
              label="Country"
              value={editedUser.country || ""}
              isEditing={isEditing}
              isEditable={true}
              onEdit={(value) => handleFieldEdit("country", value)}
            />
            {/*{Object.entries({
              address: "Address",
              city: "City",
              state: "State",
              zipcode: "Zipcode",
            }).map(([field, label]) => (
              <EditableField
                key={field}
                label={label}
                value={editedUser[field as keyof UserData] || ""}
                isEditing={isEditing}
                isEditable={true}
                onEdit={(value) =>
                  handleFieldEdit(field as keyof UserData, value)
                }
              />
            ))}*/}
          </div>
        </div>
      </DialogContent>

      {(isEditing || hasChanges) && (
        <DialogActions
          classes={{
            root: "dialog-actions user-details-actions",
          }}
        >
          <div className="buttons">
            <Button
              onClick={handleCancel}
              variant="close"
              node="Cancel"
              className="d-btn"
              disabled={isLoading}
            />
            <Button
              onClick={handleSave}
              variant="primary"
              node={isLoading ? "Saving..." : "Save"}
              className="d-btn"
              disabled={isLoading || !hasChanges || modifiedFields.size === 0}
            />
          </div>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UserDetailsModal;
